import { useState } from 'react';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { UrlObject } from 'url';
import classNames from 'classnames';

import config from 'config';
import { SortId } from 'consts/sort';
import Message from 'types/Message';
import ProductDisplayMode from 'types/ProductDisplayMode';
import SearchItem from 'types/SearchItem';
import Url from 'types/Url';
import { ProductTypeResult } from 'graphql/queries/GetProductTypes.query';
import Pagination from '@components/Pagination';
import GlassLayer from '@templates/GlassLayer';
import ShowAvailable from '@components/ShowAvailable';
import PaginationSizeSwitcher from '@components/PaginationSizeSwitcher';
import ListVariantSwitcher from '@components/ListVariantSwitcher';
import SortDrawer from '@components/SortDrawer';
import SortProducts from '@components/SortProducts';
import SearchProductsList from '@components/SearchProductsList';
import RenderInView from '@templates/RenderInView';
import { useIntl } from 'utils/intl';

import { FilterPanel, FilterDrawer, FilterBar, FilterBarProps } from './components';
import { MappedFilter } from './SearchController.utils';
import messages from './SearchController.messages';
import classes from './SearchController.module.scss';

type Props = {
  header?: Message;
  data?: SearchItem[];
  filters?: MappedFilter[];
  advancedFilters?: MappedFilter[];
  filterTags?: FilterBarProps['filterTags'];
  productTypes?: ProductTypeResult[];
  currentPage: number;
  totalPage: number;
  totalItems: number;
  paginationSize: number;
  numberOfCheckedFilters: number;
  isAvailable: boolean;
  initialFiltersValues: ParsedUrlQuery;
  loading: boolean;
  selectedSort: SortId;
  filterClearHref: Url;
  filterTagsClearHref: UrlObject;
  availableChangeHref: Url;
  getHref: (newQuery: ParsedUrlQueryInput, excludeAliasQuery?: boolean | undefined) => UrlObject;
  getPageChangeHref: (newPage: number | undefined) => Url;
  getSortChangeHref: (id: SortId) => Url;
  getLimitChangeHref: (limit: number) => UrlObject;
  getFilterChangeHref: (value: string[], key: string) => UrlObject;
  getFilterClearHref: (value: string[], key: string) => UrlObject;
  onFilterClear: () => void;
  onSortChange: (id: SortId) => void;
  onSortClear: () => void;
  onFilterChange: (value: string[], key: string) => void;
  onSubmit: (values: Record<string, string[]>) => void;
};

const Search = ({
  header,
  data = [],
  filters = [],
  advancedFilters = [],
  filterTags = [],
  productTypes,
  currentPage,
  totalPage,
  totalItems,
  paginationSize,
  isAvailable,
  availableChangeHref,
  numberOfCheckedFilters,
  initialFiltersValues = {},
  loading,
  selectedSort,
  filterClearHref,
  filterTagsClearHref,
  getHref,
  getPageChangeHref,
  getSortChangeHref,
  getLimitChangeHref,
  getFilterChangeHref,
  getFilterClearHref,
  onFilterClear,
  onSortChange,
  onSortClear,
  onFilterChange,
  onSubmit,
}: Props): JSX.Element => {
  const intl = useIntl();

  const hasResults = !!data.length;
  const [displayMode, setDisplayMode] = useState<ProductDisplayMode>('tile');

  const filterDrawer = (
    <RenderInView>
      {({ inView, ref }) => (
        <div ref={ref}>
          {inView && (
            <FilterDrawer
              numberOfCheckedFilters={numberOfCheckedFilters}
              totalItems={totalItems}
              initialValues={initialFiltersValues}
              isAvailable={isAvailable}
              filters={filters}
              advancedFilters={advancedFilters}
              productTypes={productTypes}
              availableChangeHref={availableChangeHref}
              filterTags={filterTags}
              getFilterClearHref={getFilterClearHref}
              filterClearHref={filterTagsClearHref}
              onClear={onFilterClear}
              onSubmit={onFilterChange}
              onSubmitAll={onSubmit}
            />
          )}
        </div>
      )}
    </RenderInView>
  );

  const sortDrawer = (
    <RenderInView>
      {({ inView, ref }) => (
        <div ref={ref}>
          {inView && <SortDrawer selectedItemId={selectedSort} onChangeActive={onSortChange} onClear={onSortClear} />}
        </div>
      )}
    </RenderInView>
  );

  const showAvailable = <ShowAvailable isAvailable={isAvailable} href={availableChangeHref} />;

  const paginationComponent = (
    <Pagination current={currentPage} total={totalPage} getPageChangeHref={getPageChangeHref} />
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.leftSide} data-nosnippet>
        <RenderInView>
          {({ inView, ref }) => (
            <div ref={ref} className={classes.filterPanelWrapper}>
              {inView && (
                <FilterPanel
                  filters={filters}
                  advancedFilters={advancedFilters}
                  productTypes={productTypes}
                  loading={loading}
                  numberOfCheckedFilters={numberOfCheckedFilters}
                  initialValues={initialFiltersValues}
                  filterClearHref={filterClearHref}
                  getHref={getHref}
                  getFilterChangeHref={getFilterChangeHref}
                  onSubmit={onSubmit}
                />
              )}
            </div>
          )}
        </RenderInView>
      </div>
      <div className={classes.rightSide}>
        {loading && <GlassLayer position="fixed" className={classes.loaderWrapper} />}
        {header}
        {!!filterTags.length && (
          <div className={classNames(classes.filterBar, { [classes.hideMobileFilterBar]: hasResults })} data-nosnippet>
            <FilterBar
              filterTags={filterTags}
              getFilterClearHref={getFilterClearHref}
              filterClearHref={filterTagsClearHref}
            />
          </div>
        )}
        <div className={classes.sortRow}>
          <div className={classNames(classes.sortRowColumn, classes.mobile)}>
            {filterDrawer}
            {sortDrawer}
            <ListVariantSwitcher displayMode={displayMode} onDisplayModeChange={setDisplayMode} />
          </div>
          <div className={classNames(classes.sortRowColumn, classes.desktop)}>
            <SortProducts
              onChangeActive={onSortChange}
              getSortChangeHref={getSortChangeHref}
              selectedItemId={selectedSort}
            />
            {showAvailable}
          </div>
          <div className={classNames(classes.sortRowColumn, classes.desktop)}>
            <ListVariantSwitcher displayMode={displayMode} onDisplayModeChange={setDisplayMode} />
            <div className={classes.desktop}>{paginationComponent}</div>
          </div>
        </div>
        {hasResults ? (
          <>
            <SearchProductsList data={data} displayMode={displayMode} />
            <div className={classes.footer} data-nosnippet>
              <PaginationSizeSwitcher
                numbers={config.category.paginationSizeList}
                selected={paginationSize}
                getLimitChangeHref={getLimitChangeHref}
              />
              {paginationComponent}
            </div>
          </>
        ) : (
          <ul className={classes.notFoundTips}>
            <li className={classes.notFoundTip}>{intl.formatMessage(messages.notFoundTip1)}</li>
            <li className={classes.notFoundTip}>{intl.formatMessage(messages.notFoundTip2)}</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default Search;
